import {ConfigProvider, Button as AntdButton} from 'antd';
import type {ButtonProps as AntdButtonProps} from 'antd';
import type {ButtonSemanticStyles} from 'antd/es/button/Button';
import styles from './index.module.less';
import {mergeSemanticStyles} from '@/utils/mergeSemanticStyles';

export type ButtonType = 'primary' | 'default' | 'secondary' | 'text';
export type ButtonSize = 'large' | 'middle' | 'small' | 'mini';

export interface ButtonProps extends Omit<AntdButtonProps, 'size' | 'type'> {
    /** 按钮类型：primary（一级，蓝色填充）| default（二级，半透明）| secondary（白底）| text（纯文本）。默认 default */
    type?: ButtonType;
    /** 按钮尺寸：large(40px) | middle(36px) | small(32px) | mini(28px)。默认 middle */
    size?: ButtonSize;
}

const BUTTON_THEME = {
    components: {
        Button: {
            // 尺寸
            controlHeightLG: 40, // large
            controlHeight: 36, // middle（antd 默认 32，改为 36）
            controlHeightSM: 32, // small
            borderRadius: 8,
            borderRadiusLG: 8,
            borderRadiusSM: 8,
            iconGap: 4,
            // 去掉阴影
            defaultShadow: 'none',
            primaryShadow: 'none',
            // 二级按钮（default）各状态颜色
            defaultBg: 'var(--color-fill-block-primary)',
            defaultColor: 'var(--color-text-primary)',
            defaultBorderColor: 'transparent',
            defaultHoverBg: 'var(--color-fill-block-secondary)',
            defaultHoverColor: 'var(--color-text-primary)',
            defaultHoverBorderColor: 'transparent',
            defaultActiveBg: 'var(--color-fill-block-tertiary)',
            defaultActiveColor: 'var(--color-text-primary)',
            defaultActiveBorderColor: 'transparent',
            // 一级按钮（primary）颜色
            colorPrimary: 'var(--color-primary)',
            colorPrimaryHover: 'var(--color-primary-hover)',
            colorPrimaryActive: 'var(--color-primary-active)',
            colorPrimaryBorder: 'transparent',
            colorTextLightSolid: 'var(--color-text-on-primary)',
            // 默认文本
            textTextColor: 'var(--color-text-primary)',
            textTextHoverColor: 'var(--color-text-primary)',
            textTextActiveColor: 'var(--color-text-primary)',
        },
    },
} as const;

// mini 尺寸规格（antd 无此档，通过 inline style 注入）
const MINI_STYLE: React.CSSProperties = {
    height: 28,
    fontSize: 12,
};

const ICON_ONLY_CLASS_MAP: Record<ButtonSize, string> = {
    large: styles.iconOnlyLarge,
    middle: styles.iconOnlyMiddle,
    small: styles.iconOnlySmall,
    mini: styles.iconOnlyMini,
};

/** antd 原生 type：secondary 在 antd 中不存在，映射为 default */
const ANTD_TYPE_MAP: Record<ButtonType, 'primary' | 'default' | 'text'> = {
    primary: 'primary',
    default: 'default',
    secondary: 'default',
    text: 'text',
};

const Button: React.FC<ButtonProps> = ({
    type = 'default',
    size = 'middle',
    className,
    style,
    styles: buttonStyles,
    disabled,
    ...rest
}) => {
    const isMini = size === 'mini';
    const antdSize = isMini ? 'small' : size;
    const antdType = ANTD_TYPE_MAP[type];

    const mergedStyle: React.CSSProperties = {
        ...(isMini ? MINI_STYLE : undefined),
        fontWeight: 500,
        ...style,
    };

    const hasIcon = !!rest.icon;
    const isIconOnly = hasIcon && !rest.children;

    const mergedClassName =
        [
            styles[type],
            hasIcon ? styles.hasIcon : '',
            isIconOnly ? styles.iconOnly : '',
            isIconOnly ? ICON_ONLY_CLASS_MAP[size] : '',
            className,
        ]
            .filter(Boolean)
            .join(' ') || undefined;

    return (
        <ConfigProvider theme={BUTTON_THEME}>
            <AntdButton
                type={antdType}
                size={antdSize}
                disabled={disabled}
                className={mergedClassName}
                style={mergedStyle}
                autoInsertSpace={false}
                styles={mergeSemanticStyles<ButtonSemanticStyles, AntdButtonProps>(
                    {content: {lineHeight: 1}},
                    buttonStyles,
                )}
                {...rest}
            />
        </ConfigProvider>
    );
};

export default Button;
